import axios from 'axios';
import * as Sentry from '@sentry/vue';
import { isAxiosBenignError } from '@/utils/axiosBenignError';
import { isAxiosNetworkOrTimeoutError, isAxiosRequestCanceled } from '@/utils/isAxiosRequestCanceled';

const baseURL = import.meta.env.VITE_APP_REST_API_V1;
const ABSOLUTE_HTTP_URL_REGEX = /^https?:\/\//i;
const HEADER_BLOCKLIST_FOR_EXTERNAL = ['authorization', 'x-requested-with', 'x-xsrf-token'];

function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  const target = cookies.find((row) => row.startsWith(`${name}=`));
  if (!target) return null;
  return decodeURIComponent(target.split('=').slice(1).join('='));
}

function isAbsoluteHttpUrl(url) {
  return typeof url === 'string' && ABSOLUTE_HTTP_URL_REGEX.test(url);
}

function getApiOrigin() {
  if (!isAbsoluteHttpUrl(baseURL)) return null;
  try {
    return new URL(baseURL).origin;
  } catch {
    return null;
  }
}

function getRequestOrigin(config) {
  if (!isAbsoluteHttpUrl(config?.url)) return null;
  try {
    return new URL(config.url).origin;
  } catch {
    return null;
  }
}

function removeBlockedHeadersForExternalRequest(headers) {
  if (!headers) return;

  const keys = Object.keys(headers);
  for (const key of keys) {
    const normalizedKey = String(key).toLowerCase();
    if (HEADER_BLOCKLIST_FOR_EXTERNAL.includes(normalizedKey) || normalizedKey.startsWith('x-amz-')) {
      delete headers[key];
    }
  }
}

const api = axios.create({
  baseURL: baseURL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

api.get('/sanctum/csrf-cookie').catch(() => {});

// Interceptor para adicionar o XSRF Token e o Bearer token (login Google)
api.interceptors.request.use(config => {
  const requestOrigin = getRequestOrigin(config);
  const apiOrigin = getApiOrigin();

  // Segurança: evita vazamento de credenciais/headers para URLs externas.
  if (requestOrigin && apiOrigin && requestOrigin !== apiOrigin) {
    config.withCredentials = false;
    removeBlockedHeadersForExternalRequest(config.headers);
    return config;
  }

  // Prioriza o cookie HttpOnly/secure do backend e mantém fallback legado.
  const cookieToken = getCookie('XSRF-TOKEN');
  const legacyToken = localStorage.getItem('XSRF-TOKEN');
  const token = cookieToken || legacyToken;
  if (token) {
    config.headers['X-XSRF-TOKEN'] = token;
  }
  const authToken = localStorage.getItem('auth_token');
  if (authToken) {
    config.headers['Authorization'] = `Bearer ${authToken}`;
  }
  return config;
}, error => {
  return Promise.reject(error);
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    const isNetworkFailure =
      error?.response == null
      && (
        error?.code === 'ERR_NETWORK'
        || String(error?.message || '').includes('Network Error')
      );

    if (isNetworkFailure) {
      return Promise.reject(error);
    }

    if (isAxiosRequestCanceled(error)) {
      return Promise.reject(error);
    }

    if (isAxiosNetworkOrTimeoutError(error)) {
      return Promise.reject(error);
    }

    if (isAxiosBenignError(error)) {
      return Promise.reject(error);
    }

    if (status != null && ![401, 403, 404, 422].includes(status)) {
      Sentry.captureException(error, {
        tags: { section: 'axios' },
        extra: {
          url: error?.config?.url,
          method: error?.config?.method,
          response: error?.response?.data,
        },
      });
    }

    if (status === 401) {
      console.warn('Unauthorized Request');
    }

    return Promise.reject(error);
  }
);

export default api;
